import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SxProps } from '@mui/material/styles';

export interface ExpansionPanelProps {
  title: React.ReactNode;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean; // controlled
  onChange?: (expanded: boolean) => void;
  disableGutters?: boolean;
  elevation?: number;
  square?: boolean;
  showExpandIcon?: boolean;
  titleVariant?: 'body2' | 'body1' | 'subtitle2' | 'subtitle1' | 'h6';
  containerSx?: SxProps;
  summarySx?: SxProps;
  detailsSx?: SxProps;
}

/**
 * A lightweight Expansion Panel similar to Angular Material's Expansion Panel.
 * - Collapsed state shows only the title row.
 * - Expanded state reveals the content area.
 */
export function ExpansionPanel({
  title,
  children,
  defaultExpanded = true,
  expanded,
  onChange,
  disableGutters = true,
  elevation = 0,
  square = true,
  showExpandIcon = true,
  titleVariant = 'subtitle2',
  containerSx,
  summarySx,
  detailsSx,
}: ExpansionPanelProps) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(!!defaultExpanded);
  const isControlled = typeof expanded === 'boolean';
  const isExpanded = isControlled ? !!expanded : uncontrolledExpanded;

  const handleChange = (_: React.SyntheticEvent, next: boolean) => {
    if (!isControlled) setUncontrolledExpanded(next);
    onChange?.(next);
  };

  return (
    <Accordion
      disableGutters={disableGutters}
      elevation={elevation}
      square={square}
      expanded={isExpanded}
      onChange={handleChange}
      sx={{
        m: 0,
        borderRadius: 0,
        boxShadow: 'none',
        '&:before': { display: 'none' },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: '1px solid', borderColor: 'divider' },
        '& .MuiAccordionSummary-root': { minHeight: 36, px: 2 },
        '& .MuiAccordionSummary-content': { my: 0.5 },
        '& .MuiAccordionDetails-root': { pt: 0.5, pb: 1, px: 2 },
        ...containerSx,
      }}
    >
      <AccordionSummary
        expandIcon={showExpandIcon ? <ExpandMoreIcon fontSize="small" /> : undefined}
        sx={{ px: 0, ...summarySx }}
      >
        {typeof title === 'string' ? (
          <Typography variant={titleVariant} sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        ) : (
          title
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, px: 2, pt: 0.5, pb: 1, ...detailsSx }}>{children}</AccordionDetails>
    </Accordion>
  );
};
